import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Checkbox } from "../ui/checkbox";
import { hooks } from "botframework-webchat-component";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const { useSendEvent } = hooks;

const RedeemCardArabic = ({ setScript, id, redeemData, disableSend, offerUno, language }) => {
  const sendEvent = useSendEvent();
  
  // Create storage key based on offer and user session
  const getStorageKey = () => `redeemCard_${offerUno || 'unknown'}_${id || 'default'}`;
 
  // Initialize form data from storage or defaults
  const getInitialFormData = () => {
    try {
      const storageKey = getStorageKey();
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading stored form data:', error);
    }
    
    return {
      customerName: redeemData?.customerName || "",
      customerEmail: redeemData?.customerEmail || "",
      customerContact: redeemData?.customerContact || "",
      customerAddress: "",
      orderCount: 1,
      isPOD: false
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [readOnly, setReadOnly] = useState(() => {
    try {
      const storageKey = getStorageKey();
      const submitted = sessionStorage.getItem(`${storageKey}_submitted`);
      return submitted === 'true';
    } catch (error) {
      return false;
    }
  });
  const [isSubmitted, setIsSubmitted] = useState(() => {
    try {
      const storageKey = getStorageKey();
      const submitted = sessionStorage.getItem(`${storageKey}_submitted`);
      return submitted === 'true';
    } catch (error) {
      return false;
    }
  });
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const FormSchema = z.object({
    customerName: z.string().min(2, "يجب أن يكون الاسم مكوناً من حرفين على الأقل"),
    customerEmail: z.string()
      .email("يرجى إدخال عنوان بريد إلكتروني صحيح")
      .min(5, "يجب أن يكون البريد الإلكتروني مكوناً من 5 أحرف على الأقل"),
    customerContact: z.string()
      .regex(/^05\d{8}$/, "يجب أن يكون رقم الاتصال رقماً إماراتياً صحيحاً يبدأ بـ '05' ومكوناً من 10 أرقام"),
    orderCount: z.number().min(1).max(10),
    isPOD: z.boolean()
  });

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: formData,
  });

  // Pre-fill form with data from session if available (only once when component mounts)
  useEffect(() => {
    if (redeemData && !isFormInitialized) {
      const storageKey = getStorageKey();
      const storedData = sessionStorage.getItem(storageKey);
      
      // If we have stored data, use it; otherwise use redeemData prefill
      if (!storedData) {
        const prefillData = {
          customerName: redeemData.customerName || "",
          customerEmail: redeemData.customerEmail || "",
          customerContact: redeemData.customerContact || "",
          customerAddress: "",
          orderCount: 1,
          isPOD: false
        };
        setFormData(prefillData);
        form.reset(prefillData);
        
        // Save initial prefill to storage
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(prefillData));
        } catch (error) {
          console.error('Error saving initial form data:', error);
        }
      } else {
        // Use stored data and reset form with it
        form.reset(formData);
      }
      setIsFormInitialized(true);
    }
  }, [redeemData, isFormInitialized, form, formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Save to sessionStorage whenever data changes
      try {
        const storageKey = getStorageKey();
        sessionStorage.setItem(storageKey, JSON.stringify(newData));
      } catch (error) {
        console.error('Error saving form data to storage:', error);
      }
      
      return newData;
    });
  };

  const handleSubmit = async () => {
    // Trigger validation for all fields
    const isValid = await form.trigger();
    
    if (!isValid) {
      // Form has validation errors, don't submit
      return;
    }

    const submitData = {
      ...formData,
      action: "submitRedeemOffer",
      offerUno: offerUno,
      language: language || "ar",
      offerName: redeemData?.offerName || `عرض ${offerUno}`
    };

    sendEvent("submitRedeemOffer", submitData);
    setReadOnly(true);
    setIsSubmitted(true);
    
    // Persist submission state
    try {
      const storageKey = getStorageKey();
      sessionStorage.setItem(`${storageKey}_submitted`, 'true');
    } catch (error) {
      console.error('Error saving submission state:', error);
    }
  };

  // Build script dynamically
  useEffect(() => {
    const offerDisplayName = redeemData?.offerName || `عرض ${offerUno}`;
    let visibleText = `نموذج استرداد العرض ${offerDisplayName}: يرجى إكمال المعلومات التالية لاسترداد عرضك.`;
    
    if (isSubmitted) {
      visibleText = `تم إرسال معلومات العرض ${offerDisplayName} بنجاح. سيتم التواصل معك قريباً.`;
    } else {
      visibleText += ` الاسم الكامل: ${formData.customerName || 'أدخل اسمك الكامل'}.`;
      visibleText += ` البريد الإلكتروني: ${formData.customerEmail || 'أدخل عنوان بريدك الإلكتروني'}.`;
      visibleText += ` رقم الاتصال: ${formData.customerContact || 'أدخل رقم الاتصال الخاص بك'}.`;
      visibleText += ` كمية الطلب: ${formData.orderCount}.`;
      visibleText += ` الدفع عند التسليم: ${formData.isPOD ? 'نعم' : 'لا'}.`;
      visibleText += ` زر تأكيد الاسترداد.`;
    }

    if (setScript && typeof setScript === 'function') {
      setScript(visibleText);
    }
  }, [formData, isSubmitted, setScript, redeemData, offerUno]);

  const isMobile = window.innerWidth <= 1000;

  return (
    <Card className={`border-none max-w-2xl ${isSubmitted ? 'opacity-50 pointer-events-none' : ''}`} dir="rtl">
      <CardHeader>
        <CardTitle>استرداد عرضك</CardTitle>
        <CardDescription>
          {redeemData?.offerName && (
            <div className="font-medium text-emerald-600 mb-2">
              {redeemData.offerName}
            </div>
          )}
          يرجى إكمال المعلومات التالية لاسترداد عرضك
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Form {...form}>
          <form className="space-y-4">
            {/* First row - Name and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>الاسم الكامل</FormLabel>
                    <FormControl>
                      <input
                        type="text"
                        placeholder="أدخل اسمك الكامل"
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-right"
                        {...field}
                        disabled={readOnly}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange("customerName", e.target.value);
                        }}
                        dir="rtl"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>عنوان البريد الإلكتروني  </FormLabel>
                    <FormControl>
                      <input
                        type="email"
                        placeholder="أدخل عنوان بريدك الإلكتروني"
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        {...field}
                        disabled={readOnly}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange("customerEmail", e.target.value);
                        }}
                        dir="ltr"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Second row - Contact and Order Count */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customerContact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>رقم الاتصال  </FormLabel>
                    <FormControl>
                      <input
                        type="tel"
                        placeholder="أدخل رقم الاتصال الخاص بك"
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        {...field}
                        disabled={readOnly}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange("customerContact", e.target.value);
                        }}
                        dir="ltr"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="orderCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>كمية الطلب</FormLabel>
                    <FormControl>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                        {...field}
                        disabled={readOnly}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          field.onChange(value);
                          handleInputChange("orderCount", value);
                        }}
                        dir="ltr"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

        

            {/* Fourth row - POD Checkbox */}
            <FormField
              control={form.control}
              name="isPOD"
              render={({ field }) => (
                <FormItem className="flex flex-row-reverse items-start space-x-reverse space-x-3 space-y-0 rounded-md border border-gray-200 p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        handleInputChange("isPOD", checked);
                      }}
                      disabled={readOnly}
                      className="data-[state=checked]:bg-[#027560] data-[state=checked]:border-[#027560] border-2 border-gray-300"
                      style={{
                        accentColor: '#027560',
                        height: '20px',
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none text-right">
                    <FormLabel className="text-sm font-medium">
                      الشخص ذو الإعاقة
                    </FormLabel>
                    <p className="text-xs text-gray-500">
                      ضع علامة إذا كنت من ذوي الإعاقة
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
      
      {!readOnly && (
        <CardFooter className="flex justify-center">
          <Button 
            className="w-full md:w-auto px-8 text-white font-medium rounded-full transition-all duration-200 shadow-sm hover:shadow-md text-sm" 
            disabled={disableSend}
            onClick={handleSubmit}
             style={{ 
              borderRadius: '999px',
              backgroundColor: '#027560',
              minWidth: '122px', // Ensures consistent width
              minHeight: '45px',
              ...(isMobile && {
                fontSize: '14px',
                padding: '16px 24px', // Increased height on mobile
                minWidth: '150px'
              })
            }}
            onMouseEnter={(e) => {
              if (!disableSend) {
                e.target.style.backgroundColor = '#025a54';
              }
            }}
            onMouseLeave={(e) => {
              if (!disableSend) {
                e.target.style.backgroundColor = '#027560';
              }
            }}
          >
            استرداد العرض
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default RedeemCardArabic;